import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { labAPI } from '../../utils/api';

const STAT_CARDS = [
    { key: 'total_orders_today', label: 'Total Orders',   icon: '📋', color: '#7c3aed', bg: '#f5f3ff' },
    { key: 'pending_count',      label: 'Pending',        icon: '⏳', color: '#d97706', bg: '#fffbeb' },
    { key: 'completed_count',    label: 'Completed',      icon: '✅', color: '#16a34a', bg: '#f0fdf4' },
    { key: 'stat_pending',       label: 'STAT Pending',   icon: '🚨', color: '#dc2626', bg: '#fef2f2' },
    { key: 'avg_tat_minutes',    label: 'Avg TAT (min)',  icon: '⏱️', color: '#0369a1', bg: '#eff6ff' },
];

const CAT_COLORS = ['#7c3aed','#0369a1','#059669','#d97706','#dc2626','#a21caf','#1d4ed8','#065f46'];

const pkToday = () => new Date(Date.now() + 5*60*60*1000).toISOString().split('T')[0];
const pkYesterday = () => new Date(Date.now() + 5*60*60*1000 - 86400000).toISOString().split('T')[0];

export default function LabDashboard() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [mode, setMode] = useState('date'); // 'date' or 'month'
    const [filterDate, setFilterDate] = useState(pkToday());
    const [filterMonth, setFilterMonth] = useState(() => pkToday().substring(0, 7)); // YYYY-MM

    const load = useCallback(async () => {
        setLoading(true);
        try {
            let params = {};
            if (mode === 'date') {
                params = { date: filterDate };
            } else {
                const [y, m] = filterMonth.split('-');
                params = { year: y, month: parseInt(m) };
            }
            const res = await labAPI.getDashboard(params);
            setStats(res.data);
        } catch { toast.error('Failed to load dashboard'); }
        finally { setLoading(false); }
    }, [mode, filterDate, filterMonth]);

    useEffect(() => { load(); }, [load]);

    const btnStyle = (active) => ({
        padding: '6px 14px', borderRadius: 6, border: '1px solid #e2e8f0', cursor: 'pointer',
        fontSize: 12, fontWeight: 600,
        background: active ? '#7c3aed' : 'white',
        color: active ? 'white' : '#374151',
    });

    const recentOrders = stats?.recent_orders || [];
    const byCategory = stats?.by_category || [];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                    <h2 style={{ fontSize: 20, fontWeight: 700, color: '#7c3aed', margin: 0 }}>📊 Lab Dashboard</h2>
                    <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>
                        {stats?.filter_label ? `Showing: ${stats.filter_label}` : 'Loading...'}
                    </p>
                </div>
                <button onClick={load} style={{ padding: '8px 16px', background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', fontSize: 12 }}>🔄 Refresh</button>
            </div>

            {/* Filter Bar */}
            <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                {/* Mode toggle */}
                <div style={{ display: 'flex', gap: 6 }}>
                    <button style={btnStyle(mode === 'date')} onClick={() => setMode('date')}>By Date</button>
                    <button style={btnStyle(mode === 'month')} onClick={() => setMode('month')}>By Month</button>
                </div>

                <div style={{ width: 1, height: 28, background: '#e2e8f0' }} />

                {mode === 'date' ? (
                    <>
                        {/* Quick date buttons */}
                        <button style={btnStyle(filterDate === pkToday())} onClick={() => setFilterDate(pkToday())}>Today</button>
                        <button style={btnStyle(filterDate === pkYesterday())} onClick={() => setFilterDate(pkYesterday())}>Yesterday</button>
                        {/* Date picker */}
                        <input type="date" value={filterDate}
                            onChange={e => setFilterDate(e.target.value)}
                            style={{ padding: '5px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, cursor: 'pointer' }} />
                    </>
                ) : (
                    <>
                        {/* Month picker */}
                        <input type="month" value={filterMonth}
                            onChange={e => setFilterMonth(e.target.value)}
                            style={{ padding: '5px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, cursor: 'pointer' }} />
                        <span style={{ fontSize: 12, color: '#64748b' }}>Select any month to view its data</span>
                    </>
                )}
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: 80, color: '#64748b', fontSize: 15 }}>Loading...</div>
            ) : !stats ? null : (
                <>
                    {/* Stat Cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14, marginBottom: 24 }}>
                        {STAT_CARDS.map(card => (
                            <div key={card.key} style={{ background: card.bg, border: `1px solid ${card.color}33`, borderRadius: 12, padding: '18px 16px', textAlign: 'center' }}>
                                <div style={{ fontSize: 26, marginBottom: 6 }}>{card.icon}</div>
                                <div style={{ fontSize: 28, fontWeight: 900, color: card.color }}>
                                    {stats.stats?.[card.key] != null
                                        ? (card.key === 'avg_tat_minutes' ? Math.round(stats.stats[card.key]) : stats.stats[card.key])
                                        : '0'}
                                </div>
                                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginTop: 4 }}>{card.label}</div>
                            </div>
                        ))}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
                        {/* Recent Orders */}
                        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                            <div style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0' }}>
                                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: 0 }}>
                                    Orders {mode === 'month' ? `(${stats.filter_label})` : `(${filterDate})`}
                                </h3>
                            </div>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ background: '#f8fafc' }}>
                                            {['Order #','Patient','MRN','Priority','Source','Status','Time'].map(h => (
                                                <th key={h} style={{ padding: '8px 12px', fontSize: 10, fontWeight: 700, color: '#64748b', textAlign: 'left', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recentOrders.length === 0 && (
                                            <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No orders found</td></tr>
                                        )}
                                        {recentOrders.map((o, i) => {
                                            const statusColor = o.status === 'Completed' ? '#16a34a' : o.status === 'Resulted' ? '#0369a1' : '#d97706';
                                            const priorityColor = o.priority === 'STAT' ? '#dc2626' : o.priority === 'Urgent' ? '#d97706' : '#64748b';
                                            return (
                                                <tr key={o.id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                                                    <td style={{ padding: '9px 12px', fontSize: 12, fontWeight: 700, color: '#7c3aed', whiteSpace: 'nowrap' }}>{o.order_number}</td>
                                                    <td style={{ padding: '9px 12px', fontSize: 12, fontWeight: 600 }}>{o.full_name}</td>
                                                    <td style={{ padding: '9px 12px', fontSize: 11, color: '#64748b' }}>{o.mrn}</td>
                                                    <td style={{ padding: '9px 12px' }}>
                                                        <span style={{ fontSize: 11, fontWeight: 700, color: priorityColor }}>{o.priority === 'STAT' ? '🚨 ' : ''}{o.priority}</span>
                                                    </td>
                                                    <td style={{ padding: '9px 12px', fontSize: 11, color: '#64748b' }}>{o.source}</td>
                                                    <td style={{ padding: '9px 12px' }}>
                                                        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: statusColor + '20', color: statusColor }}>{o.status}</span>
                                                    </td>
                                                    <td style={{ padding: '9px 12px', fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>
                                                        {o.order_time ? new Date(o.order_time).toLocaleString('en-PK', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : '—'}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Category Breakdown */}
                        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 }}>
                            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 16px' }}>By Category</h3>
                            {byCategory.length === 0 && <p style={{ color: '#94a3b8', fontSize: 13 }}>No data</p>}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {byCategory.map((c, i) => {
                                    const max = byCategory[0]?.count || 1;
                                    const pct = Math.round((c.count / max) * 100);
                                    const col = CAT_COLORS[i % CAT_COLORS.length];
                                    return (
                                        <div key={c.category}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                                                <span style={{ color: '#374151' }}>{c.category}</span>
                                                <span style={{ color: col, fontWeight: 700 }}>{c.count}</span>
                                            </div>
                                            <div style={{ background: '#f1f5f9', borderRadius: 4, height: 8 }}>
                                                <div style={{ width: `${pct}%`, height: 8, background: col, borderRadius: 4, transition: 'width 0.6s ease' }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
